import React, { useState, useEffect } from "react";
import { usePulseAnimation } from "../hooks/usePulseAnimation";
import { useTapTempo } from "../hooks/useTapTempo";
import { Tooltip } from "./Tooltip";

interface ControlButtonsProps {
  isRunning: boolean;
  isMuted: boolean;
  onToggle: () => void;
  onMuteToggle: () => void;
  onReset: () => void;
  onBPMChange: (bpm: number) => void;
  tapDisabled?: boolean;
  audioSettingsExpanded?: boolean;
}

export const ControlButtons: React.FC<ControlButtonsProps> = ({
  isRunning,
  isMuted,
  onToggle,
  onMuteToggle,
  onReset,
  onBPMChange,
  tapDisabled,
  audioSettingsExpanded,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  const {
    pulseState: startStopPulse,
    scaleState: startStopScale,
    triggerPulse: triggerStartStopPulse,
  } = usePulseAnimation();
  // const {
  //   pulseState: resetPulse,
  //   scaleState: resetScale,
  //   triggerPulse: triggerResetPulse,
  // } = usePulseAnimation();
  const {
    pulseState: mutePulse,
    scaleState: muteScale,
    triggerPulse: triggerMutePulse,
  } = usePulseAnimation();
  const {
    pulseState: tapPulse,
    scaleState: tapScale,
    triggerPulse: triggerTapPulse,
  } = usePulseAnimation();

  const { tapTempo, isTapping } = useTapTempo(onBPMChange);

  // Detect mobile devices where control buttons are fixed to bottom
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleStartStop = (event: React.MouseEvent<HTMLButtonElement>) => {
    triggerStartStopPulse(event);
    onToggle();
  };

  // const handleReset = (event: React.MouseEvent<HTMLButtonElement>) => {
  //   triggerResetPulse(event);
  //   resetTaps();
  //   onReset();
  // };

  const handleMuteToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    triggerMutePulse(event);
    onMuteToggle();
  };

  const handleTapTempo = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (tapDisabled) return;
    triggerTapPulse(event);
    tapTempo();
  };

  return (
    <div
      key={`control-buttons-${tapDisabled}`}
      className={`control-buttons-container ${
        audioSettingsExpanded ? "audio-expanded" : ""
      }`}
      style={{
        display: "flex",
        gap: "30px",
        marginTop: "20px",
        marginBottom: "30px",
        flexWrap: "wrap",
        justifyContent: "center",
      }}
    >
      {!isMobile ? (
        <Tooltip
          text={isRunning ? "Stop the metronome" : "Start the metronome"}
          shortcut="Space"
          position="top"
        >
          <button
            onClick={handleStartStop}
            style={{
              padding: "16px 24px",
              fontSize: "16px",
              background: isRunning
                ? "rgba(220, 38, 38, 0.2)"
                : "rgba(5, 150, 105, 0.2)",
              color: "var(--text-secondary)",
              border: "none",
              borderRadius: "12px",
              cursor: "pointer",
              fontWeight: "500",
              backdropFilter: "blur(7px)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease",
              position: "relative",
              overflow: "hidden",
              transform: `translateY(0) scale(${startStopScale.scale})`,
              width: "90px",
            }}
            onMouseEnter={(e) => {
              // Only apply hover effects on devices that actually support hover
              if (
                window.matchMedia("(hover: hover) and (pointer: fine)").matches
              ) {
                e.currentTarget.style.boxShadow =
                  "0 12px 40px rgba(0, 0, 0, 0.2)";
                e.currentTarget.style.background = isRunning
                  ? "rgba(220, 38, 38, 0.3)"
                  : "rgba(5, 150, 105, 0.3)";
              }
            }}
            onMouseLeave={(e) => {
              // Only apply hover effects on devices that actually support hover
              if (
                window.matchMedia("(hover: hover) and (pointer: fine)").matches
              ) {
                e.currentTarget.style.boxShadow =
                  "0 8px 32px rgba(0, 0, 0, 0.1)";
                e.currentTarget.style.background = isRunning
                  ? "rgba(220, 38, 38, 0.2)"
                  : "rgba(5, 150, 105, 0.2)";
              }
            }}
          >
            {/* Pulse Animation Overlay */}
            {startStopPulse.isActive && (
              <div
                style={{
                  position: "absolute",
                  left: startStopPulse.x,
                  top: startStopPulse.y,
                  width: "0",
                  height: "0",
                  borderRadius: "50%",
                  background: "var(--button-hover-bg)",
                  transform: "translate(-50%, -50%)",
                  animation: "pulseExpand 100ms ease-out forwards",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            )}
            {isRunning ? "Stop" : "Start"}
          </button>
        </Tooltip>
      ) : (
        <button
          onClick={handleStartStop}
          style={{
            padding: "16px 24px",
            fontSize: "16px",
            background: isRunning
              ? "rgba(220, 38, 38, 0.2)"
              : "rgba(5, 150, 105, 0.2)",
            color: "var(--text-secondary)",
            border: "none",
            borderRadius: "12px",
            cursor: "pointer",
            fontWeight: "500",
            backdropFilter: "blur(7px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            transition: "all 0.3s ease",
            position: "relative",
            overflow: "hidden",
            transform: `translateY(0) scale(${startStopScale.scale})`,
            width: "90px",
          }}
          onMouseEnter={(e) => {
            // Only apply hover effects on devices that actually support hover
            if (
              window.matchMedia("(hover: hover) and (pointer: fine)").matches
            ) {
              e.currentTarget.style.boxShadow =
                "0 12px 40px rgba(0, 0, 0, 0.2)";
              e.currentTarget.style.background = isRunning
                ? "rgba(220, 38, 38, 0.3)"
                : "rgba(5, 150, 105, 0.3)";
            }
          }}
          onMouseLeave={(e) => {
            // Only apply hover effects on devices that actually support hover
            if (
              window.matchMedia("(hover: hover) and (pointer: fine)").matches
            ) {
              e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.1)";
              e.currentTarget.style.background = isRunning
                ? "rgba(220, 38, 38, 0.2)"
                : "rgba(5, 150, 105, 0.2)";
            }
          }}
        >
          {/* Pulse Animation Overlay */}
          {startStopPulse.isActive && (
            <div
              style={{
                position: "absolute",
                left: startStopPulse.x,
                top: startStopPulse.y,
                width: "0",
                height: "0",
                borderRadius: "50%",
                background: "var(--button-hover-bg)",
                transform: "translate(-50%, -50%)",
                animation: "pulseExpand 100ms ease-out forwards",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
          )}
          {isRunning ? "Stop" : "Start"}
        </button>
      )}
      {/*
      <button
        onClick={handleReset}
        style={{
          padding: "12px 24px",
          fontSize: "16px",
          background: "rgba(55, 65, 81, 0.2)",
          color: "var(--text-primary)",
          border: "none",
          borderRadius: "12px",
          cursor: "pointer",
          fontWeight: "500",
          backdropFilter: "blur(7px)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "hidden",
          transform: `translateY(0) scale(${resetScale.scale})`,
          width: "90px",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 12px 40px rgba(0, 0, 0, 0.2)";
          e.currentTarget.style.background = "rgba(55, 65, 81, 0.3)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.1)";
          e.currentTarget.style.background = "rgba(55, 65, 81, 0.2)";
        }}
      >
        {resetPulse.isActive && (
          <div
            style={{
              position: "absolute",
              left: resetPulse.x,
              top: resetPulse.y,
              width: "0",
              height: "0",
              borderRadius: "50%",
              background: "var(--button-hover-bg)",
              transform: "translate(-50%, -50%)",
              animation: "pulseExpand 100ms ease-out forwards",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
        )}
        Reset
      </button>
      */}
      {!isMobile ? (
        <Tooltip
          text={isMuted ? "Unmute the metronome" : "Mute the metronome"}
          shortcut="M"
          position="top"
        >
          <button
            onClick={handleMuteToggle}
            style={{
              padding: "16px 24px",
              fontSize: "16px",
              background: isMuted
                ? "rgba(220, 38, 38, 0.2)"
                : "var(--toggle-bg)",
              color: "var(--text-secondary)",
              border: "none",
              borderRadius: "12px",
              cursor: "pointer",
              fontWeight: "500",
              backdropFilter: "blur(7px)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease",
              position: "relative",
              overflow: "hidden",
              transform: `translateY(0) scale(${muteScale.scale})`,
              width: "105px",
            }}
            onMouseEnter={(e) => {
              // Only apply hover effects on devices that actually support hover
              if (
                window.matchMedia("(hover: hover) and (pointer: fine)").matches
              ) {
                e.currentTarget.style.boxShadow =
                  "0 12px 40px rgba(0, 0, 0, 0.2)";
                e.currentTarget.style.background = isMuted
                  ? "rgba(220, 38, 38, 0.3)"
                  : "var(--button-hover-bg)";
              }
            }}
            onMouseLeave={(e) => {
              // Only apply hover effects on devices that actually support hover
              if (
                window.matchMedia("(hover: hover) and (pointer: fine)").matches
              ) {
                e.currentTarget.style.transform = `translateY(0) scale(${muteScale.scale})`;
                e.currentTarget.style.boxShadow =
                  "0 8px 32px rgba(0, 0, 0, 0.1)";
                e.currentTarget.style.background = isMuted
                  ? "rgba(220, 38, 38, 0.2)"
                  : "var(--toggle-bg)";
              }
            }}
          >
            {/* Pulse Animation Overlay */}
            {mutePulse.isActive && (
              <div
                style={{
                  position: "absolute",
                  left: mutePulse.x,
                  top: mutePulse.y,
                  width: "0",
                  height: "0",
                  borderRadius: "50%",
                  background: "var(--button-hover-bg)",
                  transform: "translate(-50%, -50%)",
                  animation: "pulseExpand 100ms ease-out forwards",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            )}
            {isMuted ? "Unmute" : "Mute"}
          </button>
        </Tooltip>
      ) : (
        <button
          onClick={handleMuteToggle}
          style={{
            padding: "16px 24px",
            fontSize: "16px",
            background: isMuted ? "rgba(220, 38, 38, 0.2)" : "var(--toggle-bg)",
            color: "var(--text-secondary)",
            border: "none",
            borderRadius: "12px",
            cursor: "pointer",
            fontWeight: "500",
            backdropFilter: "blur(7px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            transition: "all 0.3s ease",
            position: "relative",
            overflow: "hidden",
            transform: `translateY(0) scale(${muteScale.scale})`,
            width: "105px",
          }}
          onMouseEnter={(e) => {
            // Only apply hover effects on devices that actually support hover
            if (
              window.matchMedia("(hover: hover) and (pointer: fine)").matches
            ) {
              e.currentTarget.style.boxShadow =
                "0 12px 40px rgba(0, 0, 0, 0.2)";
              e.currentTarget.style.background = isMuted
                ? "rgba(220, 38, 38, 0.3)"
                : "var(--button-hover-bg)";
            }
          }}
          onMouseLeave={(e) => {
            // Only apply hover effects on devices that actually support hover
            if (
              window.matchMedia("(hover: hover) and (pointer: fine)").matches
            ) {
              e.currentTarget.style.transform = `translateY(0) scale(${muteScale.scale})`;
              e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.1)";
              e.currentTarget.style.background = isMuted
                ? "rgba(220, 38, 38, 0.2)"
                : "var(--toggle-bg)";
            }
          }}
        >
          {/* Pulse Animation Overlay */}
          {mutePulse.isActive && (
            <div
              style={{
                position: "absolute",
                left: mutePulse.x,
                top: mutePulse.y,
                width: "0",
                height: "0",
                borderRadius: "50%",
                background: "var(--button-hover-bg)",
                transform: "translate(-50%, -50%)",
                animation: "pulseExpand 100ms ease-out forwards",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
          )}
          {isMuted ? "Unmute" : "Mute"}
        </button>
      )}
      <div className={`tap-button-wrapper ${!tapDisabled ? "visible" : ""}`}>
        {!isMobile ? (
          <Tooltip text="Tap to set tempo" position="top">
            <button
              onClick={handleTapTempo}
              className={isTapping ? "tap-button-active" : ""}
              style={{
                padding: "16px 24px",
                fontSize: "16px",
                background: isTapping
                  ? "var(--button-hover-bg)"
                  : "var(--toggle-bg)",
                color: "var(--text-secondary)",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: "500",
                backdropFilter: "blur(7px)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                transform: `translateY(0) scale(${tapScale.scale})`,
                width: "80px",
              }}
              onMouseEnter={(e) => {
                // Only apply hover effects on devices that actually support hover
                if (
                  window.matchMedia("(hover: hover) and (pointer: fine)")
                    .matches
                ) {
                  e.currentTarget.style.boxShadow =
                    "0 12px 40px rgba(0, 0, 0, 0.2)";
                  e.currentTarget.style.background = "var(--button-hover-bg)";
                }
              }}
              onMouseLeave={(e) => {
                // Only apply hover effects on devices that actually support hover
                if (
                  window.matchMedia("(hover: hover) and (pointer: fine)")
                    .matches
                ) {
                  e.currentTarget.style.boxShadow =
                    "0 8px 32px rgba(0, 0, 0, 0.1)";
                  e.currentTarget.style.background = isTapping
                    ? "var(--button-hover-bg)"
                    : "var(--toggle-bg)";
                }
              }}
            >
              {/* Pulse Animation Overlay */}
              {tapPulse.isActive && (
                <div
                  style={{
                    position: "absolute",
                    left: tapPulse.x,
                    top: tapPulse.y,
                    width: "0",
                    height: "0",
                    borderRadius: "50%",
                    background: "var(--button-hover-bg)",
                    transform: "translate(-50%, -50%)",
                    animation: "pulseExpand 100ms ease-out forwards",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                />
              )}
              Tap
            </button>
          </Tooltip>
        ) : (
          <button
            onClick={handleTapTempo}
            className={isTapping ? "tap-button-active" : ""}
            style={{
              padding: "16px 24px",
              fontSize: "16px",
              background: isTapping
                ? "var(--button-hover-bg)"
                : "var(--toggle-bg)",
              color: "var(--text-secondary)",
              border: "none",
              borderRadius: "12px",
              cursor: "pointer",
              fontWeight: "500",
              backdropFilter: "blur(7px)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease",
              position: "relative",
              overflow: "hidden",
              transform: `translateY(0) scale(${tapScale.scale})`,
              width: "80px",
            }}
            onMouseEnter={(e) => {
              // Only apply hover effects on devices that actually support hover
              if (
                window.matchMedia("(hover: hover) and (pointer: fine)").matches
              ) {
                e.currentTarget.style.boxShadow =
                  "0 12px 40px rgba(0, 0, 0, 0.2)";
                e.currentTarget.style.background = "var(--button-hover-bg)";
              }
            }}
            onMouseLeave={(e) => {
              // Only apply hover effects on devices that actually support hover
              if (
                window.matchMedia("(hover: hover) and (pointer: fine)").matches
              ) {
                e.currentTarget.style.boxShadow =
                  "0 8px 32px rgba(0, 0, 0, 0.1)";
                e.currentTarget.style.background = isTapping
                  ? "var(--button-hover-bg)"
                  : "var(--toggle-bg)";
              }
            }}
          >
            {/* Pulse Animation Overlay */}
            {tapPulse.isActive && (
              <div
                style={{
                  position: "absolute",
                  left: tapPulse.x,
                  top: tapPulse.y,
                  width: "0",
                  height: "0",
                  borderRadius: "50%",
                  background: "var(--button-hover-bg)",
                  transform: "translate(-50%, -50%)",
                  animation: "pulseExpand 100ms ease-out forwards",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            )}
            Tap
          </button>
        )}
      </div>

      {/* Click Style Dropdown removed - moved to ToggleControls */}
    </div>
  );
};
